import { Button, IconButton, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldUser, EldVehicle } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import { useAuthContext } from "src/auth/hooks";


type EldUsersProps = {
  eldUsers?: EldUser[],
  onDeleteEldUserSelected: (a: EldUser) => void,
}

const EldUsers = memo(({ eldUsers, onDeleteEldUserSelected }: EldUsersProps) => {
  const { sortedList, sortData } = useSort(eldUsers, "name");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["name", "email"]);

  const authContext = useAuthContext();

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Users" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell />
              <SortableHeader sortKey="name" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="email" sortData={sortData} >
                Email
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldUser, index) =>
            (<TableRow
              key={index}
            >
              <TruxTableCell align="left">
                {authContext?.session?.organization?.ownerId !== eldUser.id &&
                  <IconButton size="small" color="error" onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeleteEldUserSelected(eldUser)
                  }}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                }
              </TruxTableCell>

              <TruxTableCell>
                {eldUser.name}
              </TruxTableCell>

              <TruxTableCell>
                {eldUser.email}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldUsers?: EldUser[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldUsers: axios.get(endpoints.eldUsers.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);

  const selectedEldUserModel = useModelSavingData<EldUser>(
    '',
    endpoints.eldUsers.create,
    endpoints.eldUsers.delete,
  );

  const updateEldUser = () => {
    selectedEldUserModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the User");
        const eldUser = response.data as EldUser;

        updateData("eldUsers")(replaceById(data!.eldUsers!, eldUser));

        setIsOpen(false);
      }
    })
  }


  const deleteEldUser = () => {
    selectedEldUserModel.destroy().then(response => {
      if (response) {
        enqueueSnackbar("Successfully Removed the User");
        updateData("eldUsers")(deleteById(data!.eldUsers!, selectedEldUserModel.model!));

        setDeleteIsOpen(false);
      }
    })
  }


  const onEldUserSelected = useCallback((a: EldUser) => {
    selectedEldUserModel.set({
      ...a,
      newPassword: "",
      newPasswordConfirm: ""
    });
    setIsOpen(true);
  }, [selectedEldUserModel, setIsOpen]);

  const onDeleteEldUserSelected = useCallback((a: EldUser) => {
    selectedEldUserModel.set({
      ...a,
      newPassword: "",
      newPasswordConfirm: ""
    });
    setDeleteIsOpen(true);
  }, [selectedEldUserModel, setDeleteIsOpen]);


  const onNewEldUserSelected = useCallback(() => {
    selectedEldUserModel.set({
      id: 0,
      name: "",
      email: "",

      newPassword: "",
      newPasswordConfirm: "",
    });
    setIsOpen(true);
  }, [selectedEldUserModel]);

  const newEldUserButton = <Button
    onClick={onNewEldUserSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    Add User
  </Button>;


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldUsers?.length !== 0 &&
          <PageHeader title="Manage Users">
            {newEldUserButton}
          </PageHeader>
        }

        {data && data?.eldUsers?.length === 0 &&
          <NoneFound title="No Active Users Found" message="To add an User click the 'Add User' button below.">
            {newEldUserButton}
          </NoneFound>
        }

        {data && data?.eldUsers?.length !== 0 && <EldUsers eldUsers={data.eldUsers} onDeleteEldUserSelected={onDeleteEldUserSelected} />}

        {selectedEldUserModel.model &&
          <SimpleDialog
            title="Remove User"
            open={deleteIsOpen}
            onCancel={() => setDeleteIsOpen(false)}
            onSubmit={deleteEldUser}
            submitDisabled={selectedEldUserModel.loading}
            submitButton="Remove User"
            submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
            submitButtonColor="error">

            <DisplayErrors savingData={selectedEldUserModel} />

            Are you sure you would like to remove {selectedEldUserModel.model?.email}?
          </SimpleDialog>
        }


        {data && selectedEldUserModel.model && <SimpleDialog
          title={selectedEldUserModel.isNew ? "New User" : "Update User"}
          open={isOpen}
          size="medium"
          onCancel={() => {
              setIsOpen(false);
          }}
          onSubmit={updateEldUser}
          submitDisabled={selectedEldUserModel.loading}
          submitButton={selectedEldUserModel.isNew ? "New User" : "Update User"}
          submitButtonIcon={selectedEldUserModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
          submitButtonColor={selectedEldUserModel.isNew ? "success" : "info"}>
            <DisplayErrors savingData={selectedEldUserModel} />

            <Stack direction="column" gap={1}>

            <TruxFormField label="Name *">
                <TruxTextField modelSavingData={selectedEldUserModel} field="name" />
            </TruxFormField>

            <TruxFormField label="Email *">
                <TruxTextField modelSavingData={selectedEldUserModel} field="email" />
            </TruxFormField>
            <TruxFormField label={`Password${!selectedEldUserModel.model?.id ? " *" : ""}`}>
                <TruxTextField modelSavingData={selectedEldUserModel} field="newPassword" />
                {!!selectedEldUserModel.model?.id && <Typography fontSize="small" fontStyle="italic">
                * if the user already exists the new password will not be used
                </Typography>}
            </TruxFormField>
            {(selectedEldUserModel.model?.newPassword || "").length > 0 && <TruxFormField label={`Confirm Password${!selectedEldUserModel.model?.id ? " *" : ""}`}>
                <TruxTextField modelSavingData={selectedEldUserModel} field="newPasswordConfirm" />
            </TruxFormField>}
          </Stack>
        </SimpleDialog>}
      </DisplayPageData>
    </Container >
  );
}
